import { FEATURE_TYPE } from '@/constants/features';
import { MapInstance } from '@/types/map';
import { UsePointToProjectionResult } from '@/utils/map/usePointToProjection';
import { addAlphaToHexString } from '@/utils/convert/addAlphaToHexString';
import getStyle from '@/components/Map/MapViewer/utils/mapElementsRendering/style/getStyle';
import { Feature } from 'ol';
import { FeatureLike } from 'ol/Feature';
import Polygon from 'ol/geom/Polygon';
import Style from 'ol/style/Style';
import type { MarkerSurface } from 'minerva-plugin-api';

/* eslint-disable no-magic-numbers */
const getZoomScaledStyle = (
  mapInstance: MapInstance,
): ((feature: FeatureLike, resolution: number) => Style | Style[] | void) => {
  return (feature, resolution) => {
    const maxZoom = mapInstance?.getView().get('originalMaxZoom');
    const minResolution = mapInstance?.getView().getResolutionForZoom(maxZoom);
    const style = feature.get('style');
    if (!minResolution || !style) {
      return [];
    }

    const scale = minResolution / resolution;
    const lineWidth = feature.get('lineWidth') * scale;

    if (style instanceof Style && style.getStroke()) {
      style.getStroke()?.setWidth(lineWidth);
    }
    return style;
  };
};

export const getMarkerSurfaceFeatures = (
  markers: MarkerSurface[],
  {
    pointToProjection,
    mapInstance,
  }: { pointToProjection: UsePointToProjectionResult; mapInstance: MapInstance },
): Feature<Polygon>[] =>
  markers.map(marker => {
    const polygon = new Polygon([
      [
        pointToProjection({ x: marker.x, y: marker.y + marker.height }),
        pointToProjection({ x: marker.x + marker.width, y: marker.y + marker.height }),
        pointToProjection({ x: marker.x + marker.width, y: marker.y }),
        pointToProjection({ x: marker.x, y: marker.y }),
      ],
    ]);
    const style = getStyle({
      geometry: polygon,
      fillColor: addAlphaToHexString(marker.color, marker.opacity),
      lineWidth: 1,
      zIndex: 99999,
    });
    const feature = new Feature<Polygon>({
      geometry: polygon,
      id: marker.id,
      type: FEATURE_TYPE.SURFACE_MARKER,
      style,
      lineWidth: 1,
    });
    feature.setStyle(getZoomScaledStyle(mapInstance));
    return feature;
  });
